/**
 * Runtime Config Entry Row
 *
 * One file/env entry in the RuntimeConfigDialog. Secret values are
 * write-only: the textarea starts empty and saved state is shown via metadata
 * only (is_set, size, updated_at/by). Non-secret values (#127) come back from
 * the API, so the textarea is prefilled and edited in place; an inherited one
 * is shown read-only until the admin chooses Edit (which confirms the fork).
 *
 * `editable`/`canDelete` are computed by the dialog from the ownership
 * model (owned_by_context + whether the user has acknowledged a fork), not
 * derived here — the row just renders what it is told. Save is always
 * available (even on an inherited, unowned entry) since submitting it is
 * exactly what forks a copy at this level; `editable` only changes the
 * hint text so the user knows what saving will do.
 */

import { useEffect, useState, type ReactElement } from 'react';
import type { RuntimeConfigEntry } from '../../schemas/admin.schemas';

interface RuntimeConfigEntryRowProps {
  entry: RuntimeConfigEntry;
  onSave: (value: string) => Promise<void>;
  onDelete: () => void;
  onRequestEdit: () => void;
  isSaving: boolean;
  isDeleting: boolean;
  editable: boolean;
  canDelete: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

function getStatus(entry: RuntimeConfigEntry): { label: string; className: string } {
  if (entry.is_set) {
    return { label: 'Set', className: 'runtime-config__badge--global' };
  }
  if (entry.required) {
    return { label: 'Required — not set', className: 'runtime-config__badge--required' };
  }
  return { label: 'Not set', className: 'runtime-config__badge--unset' };
}

export function RuntimeConfigEntryRow({
  entry,
  onSave,
  onDelete,
  onRequestEdit,
  isSaving,
  isDeleting,
  editable,
  canDelete,
}: RuntimeConfigEntryRowProps): ReactElement {
  const readable = !entry.secret;
  const saved = entry.value ?? '';
  const [value, setValue] = useState(saved);
  // A save, delete or revert refetches the entry; show what is now stored.
  useEffect(() => { setValue(saved); }, [saved]);
  const status = getStatus(entry);
  const readOnly = readable && entry.is_set && !editable;
  const placeholder = editable
    ? (entry.is_set ? 'Enter a new value to replace it…' : 'Enter a value…')
    : 'Enter a value to fork an independent copy…';

  const handleSave = (): void => {
    if (!value || value === saved) return;
    // Only clear on success — a failed save (e.g. a 422 on a pasted
    // kubeconfig) must leave the user's input in place so they can fix one
    // field and retry instead of retyping everything. The error itself is
    // surfaced by the dialog-level banner.
    void onSave(value).then(() => { if (!readable) setValue(''); }).catch(() => {});
  };

  return (
    <li className="runtime-config__row">
      <div className="runtime-config__row-header">
        <span className={`runtime-config__badge ${status.className}`}>{status.label}</span>
        <div className="runtime-config__identity">
          <span className="runtime-config__name">
            {entry.label || entry.name}
            <span className="runtime-config__subtext"> ({entry.name})</span>
          </span>
          {entry.kind === 'file' && entry.target_path && (
            <span className="runtime-config__path">
              {entry.target_path}
              {entry.file_mode ? ` (${entry.file_mode})` : ''}
            </span>
          )}
          {entry.description && (
            <span className="runtime-config__description">{entry.description}</span>
          )}
        </div>
      </div>
      {entry.is_set && (
        <div className="runtime-config__meta">
          {entry.size_bytes != null && <span>{formatBytes(entry.size_bytes)}</span>}
          {entry.updated_at && (
            <span>
              Updated {new Date(entry.updated_at).toLocaleString()}
              {entry.updated_by ? ` by ${entry.updated_by}` : ''}
            </span>
          )}
        </div>
      )}
      <div className="runtime-config__edit">
        <textarea
          className="runtime-config__textarea"
          value={value}
          onChange={(e) => { setValue(e.target.value); }}
          rows={entry.kind === 'file' ? (readable ? 12 : 3) : 1}
          placeholder={placeholder}
          readOnly={readOnly}
          aria-label={readable ? `Value of ${entry.name}` : `New value for ${entry.name}`}
        />
        <div className="runtime-config__actions">
          {readOnly ? (
            <button type="button" className="dialog__btn dialog__btn--primary" onClick={onRequestEdit}>
              Edit
            </button>
          ) : (
            <button
              type="button"
              className="dialog__btn dialog__btn--primary"
              disabled={!value || value === saved || isSaving}
              onClick={handleSave}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="feature-table__edit-btn feature-table__edit-btn--danger"
              disabled={isDeleting}
              onClick={onDelete}
              aria-label={`Delete ${entry.name}`}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
